import React, { FormEventHandler } from "react";
import Modal from "react-modal";
import { Link } from "react-router-dom";
import Select from "components/select/Select";
import DateInput from "components/dateInput/DateInput";
import { addMeet } from "util/pouchActions";
import {
  getUnitOptions,
  getFederationOptions,
  getDateFormatOptions,
} from "util/options";
import "./NewMeetModal.scss";
import isEmpty from "lodash/isEmpty";
import { parseDateString } from "util/dateHelper";
import { isBefore, subDays } from "date-fns";

type NewMeetModalState = {
  contactEmail: string;
  meetName: string;
  meetDate: string;
  units: string;
  federation: string;
  dateFormat: string;
  errors: Record<string, string>;
};

const getInitialState = (): NewMeetModalState => {
  return {
    contactEmail: "",
    meetName: "",
    meetDate: "",
    units: "KG",
    federation: "USAPL",
    dateFormat: "MM/DD/YYYY",
    errors: {},
  };
};

const NewMeetModal = ({
  onRequestClose,
  isOpen,
}: {
  isOpen: boolean;
  onRequestClose: () => void;
}) => {
  const [state, setState] = React.useState<NewMeetModalState>(
    getInitialState()
  );

  const handleChange = (name: string, value: any) => {
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const createMeet: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const errors: Record<string, string> = {};
    const { meetName, meetDate, contactEmail, units, federation, dateFormat } =
      state;

    if (!meetName) {
      errors.meetName = "Meet Name is required.";
    }

    if (!meetDate) {
      errors.meetDate = "Meet Date is required.";
    }

    try {
      const meetDateDate = parseDateString({
        dateString: meetDate,
        meet: { dateFormat },
      });

      if (meetDateDate && isBefore(meetDateDate, subDays(new Date(), 7))) {
        errors.meetDate = "Meet Date must be in the future.";
      }
    } catch (e) {
      console.log(e);
    }

    if (!contactEmail) {
      errors.contactEmail = "Contact Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(contactEmail)) {
      errors.contactEmail = "Contact Email is not valid.";
    }

    if (isEmpty(errors)) {
      const meetId = addMeet({
        name: meetName,
        date: meetDate,
        contactEmail: contactEmail.toLowerCase(),
        units: units,
        federation: federation,
        dateFormat: dateFormat,
      });

      try {
        // @ts-expect-error
        if (typeof gtag !== "undefined") {
          // eslint-disable-next-line no-undef
          // @ts-expect-error
          gtag("event", "create_meet", {
            meetId,
            name: meetName,
            date: meetDate,
            contactEmail: contactEmail,
            units: units,
            federation: federation,
            dateFormat: dateFormat,
          });
        }
      } catch (e) {
        console.log(e);
      }

      setState(getInitialState());
      onRequestClose();
    } else {
      setState((prev) => ({ ...prev, errors }));
    }
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      contentLabel="New Meet"
      onRequestClose={onRequestClose}
      className={{
        base: "new-meet-modal",
        afterOpen: "new-meet-modal-after-open",
        beforeClose: "new-meet-modal-before-close",
      }}
      overlayClassName={{
        base: "modal-overlay",
        afterOpen: "",
        beforeClose: "",
      }}
    >
      <form onSubmit={createMeet}>
        <div className="content">
          <div className="title">Create New Meet</div>
          <div className="info">
            <p>
              This will create a new meet stored locally in your browser. You
              can share your meet with others by uploading from the setup page.
            </p>
            <p>
              Please read the <Link to="/about">FAQ</Link> and{" "}
              <Link to="/instructions">instructions</Link>. This app is
              currently under development and things may have changed since you
              last used the app.
            </p>
          </div>

          <div className="row">
            <label>
              Meet Name *
              <div className="input-wrapper">
                <input
                  type="text"
                  value={state.meetName}
                  onChange={(e) => handleChange("meetName", e.target.value)}
                />
              </div>
              <div className="error-message">{state.errors.meetName}</div>
            </label>
          </div>
          <div className="row">
            <label>
              Date Format *
              <div className="input-wrapper">
                <Select
                  name="dateFormat"
                  value={state.dateFormat}
                  options={getDateFormatOptions()}
                  onChange={(value) => handleChange("dateFormat", value)}
                  clearable={false}
                />
              </div>
            </label>
            <div className="error-message">{state.errors.dateFormat}</div>
          </div>
          <div className="row">
            <label>
              Meet Date *
              <div className="input-wrapper">
                <DateInput
                  dateFormat={state.dateFormat}
                  value={state.meetDate}
                  onChange={(value) => handleChange("meetDate", value)}
                  placeholder={state.dateFormat}
                />
              </div>
            </label>
            <div className="error-message">{state.errors.meetDate}</div>
          </div>
          <div className="row">
            <label>
              Units *
              <div className="input-wrapper">
                <Select
                  name="units"
                  value={state.units}
                  options={getUnitOptions()}
                  onChange={(value) => handleChange("units", value)}
                  clearable={false}
                />
              </div>
            </label>
            <div className="error-message">{state.errors.units}</div>
          </div>
          <div className="row">
            <label>
              Federation *
              <div className="input-wrapper">
                <Select
                  name="federation"
                  value={state.federation}
                  options={getFederationOptions()}
                  onChange={(value) => handleChange("federation", value)}
                  clearable={false}
                />
              </div>
            </label>
            <div className="error-message">{state.errors.federation}</div>
          </div>
          <div className="row">
            <label>
              Contact Email *
              <div className="input-wrapper">
                <input
                  type="text"
                  value={state.contactEmail}
                  onChange={(e) => handleChange("contactEmail", e.target.value)}
                />
              </div>
            </label>
            <div className="error-message">{state.errors.contactEmail}</div>
          </div>
        </div>
        <div className="button-row">
          <button
            type="button"
            style={{ marginRight: 12 }}
            onClick={onRequestClose}
          >
            Cancel
          </button>
          <button type="submit">Create Meet</button>
        </div>
      </form>
    </Modal>
  );
};

export default NewMeetModal;
